import { useEffect, useRef, useState, type JSX } from 'react';
import { onNavClick, useRouteHref } from '../lib/router';
import { api } from '../api/client';
import { markGlobalProjectAvailable, setGlobalProject, useGlobalProject } from '../lib/global-project';
import { IconHome, IconProjects, IconTerminal, IconInbox, IconMore, IconBolt } from './icons';
import { EnginePosturePanel } from './EnginePosturePanel';
import { GlobalPauseControl, useGlobalAutonomyPosture } from './GlobalPauseControl';
import { RestartPrepChip, restartChipFor, useRestartPrepView } from './RestartPrepChip';
import { unseenNovidades } from './NovidadesPage';
import { OwnerWaitingAccess } from './OwnerWaitingAccess';

const primary = [
  // 02/10/2026 (8c2a8862) — a aba do Assistente virou Hoje (visão executiva); a conversa segue em /chat, link no topo de Hoje e no Mais.
  { href: '/hoje', label: 'Hoje', Icon: IconHome },
  { href: '/deck', label: 'Projetos', Icon: IconProjects },
  { href: '/operations', label: 'Operações', Icon: IconBolt },
  { href: '/product', label: 'Produto', Icon: IconTerminal },
  { href: '/sessoes', label: 'Gestão', Icon: IconTerminal },
  { href: '/central', label: 'Atenção', Icon: IconInbox },
];
const groups = [
  {
    title: 'Trabalho',
    links: [
      ['/donizeti', 'Donizeti (orquestrador)'],
      ['/chat', 'Assistente (conversa)'],
      ['/apontamentos', 'Apontamentos'],
      ['/novidades', 'Novidades'],
      ['/sessoes?vista=mapa', 'Mapa e correio'],
      ['/sessoes?vista=trabalho', 'Espaço de trabalho'],
      ['/cerebro', 'Segundo cérebro'],
      ['/app', 'App Android'],
      ['/workspace', 'Frentes paralelas'],
      ['/backlog', 'Planejado e entregue'],
      ['/approvals', 'Comunicação'],
      ['/evidence', 'Evidências'],
    ],
  },
  {
    // AL.E4 (AUD.24) — quatro destinos viraram um: /health (desempenho, execuções, instruções, dispositivos em abas).
    title: 'Operação',
    links: [
      ['/health', 'Saúde do sistema'],
      ['/consumo', 'Consumo por projeto'],
      ['/autonomy', 'Autonomia'],
      ['/reiniciar', 'Reiniciar o computador'],
    ],
  },
];

/**
 * AL.E4 — PROJETO GLOBAL: escolhido uma vez aqui, vale como escolha inicial de /backlog, do destino do apontamento
 * em /central e de /evidence. `variant="bar"` vive na barra (desktop); `variant="more"` vive dentro do "Mais" (celular).
 */
function GlobalProjectSelect({ variant, projects }: { variant: 'bar' | 'more'; projects: Array<{ cwd: string; name: string; category: string }> }): JSX.Element | null {
  const current = useGlobalProject();
  if (projects.length === 0) return null;
  const id = `global-project-${variant}`;
  return (
    <div className={`global-project global-project--${variant}`}>
      <label htmlFor={id}>projeto</label>
      <select
        id={id}
        value={projects.some((p) => p.cwd === current) ? current : ''}
        onChange={(e) => setGlobalProject(e.target.value)}
        data-testid={variant === 'bar' ? 'global-project-select' : 'global-project-select-mobile'}
      >
        <option value="">— nenhum —</option>
        {projects.map((p) => (
          <option key={p.cwd} value={p.cwd}>
            {p.name} · {p.category}
          </option>
        ))}
      </select>
    </div>
  );
}
export function ProductNavigation(): JSX.Element {
  const href = useRouteHref();
  const url = new URL(href, window.location.origin);
  const path =
    url.pathname === '/' ? (url.searchParams.has('focus') ? '/deck' : '/chat') : url.pathname;
  const [open, setOpen] = useState(false);
  const [posture, setPosture] = useGlobalAutonomyPosture();
  const restart = useRestartPrepView();
  const restartAlert = restartChipFor(restart.view, restart.now) !== null;
  const [projects, setProjects] = useState<Array<{ cwd: string; name: string; category: string }>>([]);
  useEffect(() => {
    let alive = true;
    api
      .listProjects()
      .then((list) => {
        if (!alive) return;
        setProjects(list.map((p) => ({ cwd: p.cwd, name: p.name, category: p.category })));
        markGlobalProjectAvailable(list.length > 0);
      })
      .catch(() => {
        /* sem lista: o seletor não aparece; as telas seguem com a escolha própria */
      });
    return () => {
      alive = false;
    };
  }, []);
  const host = useRef<HTMLElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [href]);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent): void => {
      if (!host.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        host.current?.querySelector<HTMLButtonElement>('.product-more-trigger')?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  return (
    <><nav className="product-navigation" aria-label="Navegação principal" ref={host}>
      <a
        className="product-brand"
        href="/chat"
        onClick={onNavClick('/chat')}
        aria-label="term-deck — assistente"
      >
        ▟ <span>term-deck</span>
      </a>
      <div className="product-primary">
        {primary.map(({ href: target, label, Icon }) => (
          <a
            key={target}
            href={target}
            onClick={onNavClick(target)}
            aria-current={path === target && !url.searchParams.has('master') ? 'page' : undefined}
          >
            <Icon />
            <span>{label}</span>
          </a>
        ))}
      </div>
      <GlobalProjectSelect variant="bar" projects={projects} />
      <GlobalPauseControl view={posture} onChange={setPosture} />
      <RestartPrepChip view={restart.view} now={restart.now} variant="bar" />
      <button
        type="button"
        className={`product-more-trigger${posture?.ativa ? ' has-pause-alert' : ''}${restartAlert ? ' has-restart-alert' : ''}`}
        aria-expanded={open}
        aria-controls="product-more"
        onClick={() => setOpen(!open)}
      >
        <IconMore />
        <span>Mais</span>
      </button>
      {open && (
        <div className="product-more" id="product-more">
          {/* No celular a barra nao comporta mais um item: a mesma chave vive no topo do "Mais". */}
          {restartAlert ? (
            <section className="product-more-restart">
              <h2>Reinício do computador</h2>
              <RestartPrepChip view={restart.view} now={restart.now} variant="more" />
            </section>
          ) : null}
          <section className="product-more-pause">
            <h2>Pausa global</h2>
            <EnginePosturePanel onStateChange={setPosture} />
          </section>
          <section className="product-more-project">
            <h2>Projeto</h2>
            <GlobalProjectSelect variant="more" projects={projects} />
          </section>
          {groups.map((group) => (
            <section key={group.title}>
              <h2>{group.title}</h2>
              {group.links.map(([target, label]) => (
                <a
                  key={target}
                  href={target}
                  onClick={onNavClick(target!)}
                  aria-current={url.pathname + url.search === target ? 'page' : undefined}
                >
                  {label}
                  {target === '/novidades' && unseenNovidades() > 0 ? <span className="nav-new-count" aria-label={`${unseenNovidades()} ainda não abertas`}>{unseenNovidades()}</span> : null}
                </a>
              ))}
            </section>
          ))}
          <details className="product-legacy"><summary>Suporte legado</summary><a href="/central?tab=master" onClick={onNavClick('/central?tab=master')}>MASTER operacional · legado</a><a href="/orchestrator" onClick={onNavClick('/orchestrator')}>Orquestrador manual · legado</a></details>
        </div>
      )}
    </nav>{url.pathname !== '/donizeti' && <OwnerWaitingAccess />}</>
  );
}
